// frontend/src/components/playground/PlaygroundCharacter.jsx
/**
 * Playground Character - Human-like character avatar for the guide
 * Supports male/female preference with customizable appearance
 */

import React from 'react';
import { motion } from 'framer-motion';

const PlaygroundCharacter = ({ 
  gender = 'neutral', 
  size = 'md',
  animated = true,
  expression = 'happy' // happy, thinking, excited, calm
}) => {
  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-16 h-16',
    lg: 'w-24 h-24',
    xl: 'w-32 h-32'
  };

  const getCharacterSVG = () => {
    // Male character
    if (gender === 'male') {
      return (
        <svg viewBox="0 0 100 100" className="w-full h-full">
          {/* Face */}
          <circle cx="50" cy="50" r="45" fill="#FDB813" stroke="#E6A000" strokeWidth="2"/>
          
          {/* Hair */}
          <path d="M 20 30 Q 20 15, 35 20 Q 50 15, 65 20 Q 80 15, 80 30 Q 80 25, 75 25 Q 70 20, 65 25 Q 50 20, 35 25 Q 30 20, 25 25 Q 20 25, 20 30 Z" 
                fill="#4A3728" stroke="#3A2A1F" strokeWidth="1"/>
          
          {/* Eyes */}
          {expression === 'happy' ? (
            <>
              <path d="M 35 45 Q 40 50, 35 55" stroke="#333" strokeWidth="2" fill="none" strokeLinecap="round"/>
              <path d="M 65 45 Q 70 50, 65 55" stroke="#333" strokeWidth="2" fill="none" strokeLinecap="round"/>
            </>
          ) : expression === 'thinking' ? (
            <>
              <circle cx="40" cy="50" r="3" fill="#333"/>
              <circle cx="60" cy="50" r="3" fill="#333"/>
              <path d="M 40 58 Q 50 62, 60 58" stroke="#333" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
            </>
          ) : (
            <>
              <circle cx="40" cy="50" r="4" fill="#333"/>
              <circle cx="60" cy="50" r="4" fill="#333"/>
            </>
          )}
          
          {/* Nose */}
          <ellipse cx="50" cy="60" rx="3" ry="2" fill="#E6A000"/>
          
          {/* Mouth */}
          {expression === 'happy' ? (
            <path d="M 40 70 Q 50 78, 60 70" stroke="#333" strokeWidth="2" fill="none" strokeLinecap="round"/>
          ) : expression === 'excited' ? (
            <ellipse cx="50" cy="72" rx="8" ry="6" fill="#333"/>
          ) : (
            <path d="M 40 70 Q 50 72, 60 70" stroke="#333" strokeWidth="2" fill="none" strokeLinecap="round"/>
          )}
        </svg>
      );
    }
    
    // Female character
    if (gender === 'female') {
      return (
        <svg viewBox="0 0 100 100" className="w-full h-full">
          {/* Face */}
          <circle cx="50" cy="50" r="45" fill="#FFDBAC" stroke="#E6C99A" strokeWidth="2"/>
          
          {/* Hair */}
          <path d="M 15 25 Q 15 10, 30 15 Q 50 10, 70 15 Q 85 10, 85 25 Q 85 20, 80 20 Q 75 15, 70 20 Q 50 15, 30 20 Q 25 15, 20 20 Q 15 20, 15 25 Z" 
                fill="#8B4513" stroke="#654321" strokeWidth="1"/>
          <path d="M 20 25 Q 25 30, 30 25 Q 50 30, 70 25 Q 75 30, 80 25 Q 80 35, 75 40 Q 50 35, 25 40 Q 20 35, 20 25 Z" 
                fill="#8B4513"/>
          
          {/* Eyes */}
          {expression === 'happy' ? (
            <>
              <path d="M 35 45 Q 40 50, 35 55" stroke="#333" strokeWidth="2" fill="none" strokeLinecap="round"/>
              <path d="M 65 45 Q 70 50, 65 55" stroke="#333" strokeWidth="2" fill="none" strokeLinecap="round"/>
            </>
          ) : expression === 'thinking' ? (
            <>
              <circle cx="40" cy="50" r="3" fill="#333"/>
              <circle cx="60" cy="50" r="3" fill="#333"/>
              <path d="M 40 58 Q 50 62, 60 58" stroke="#333" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
            </>
          ) : (
            <>
              <circle cx="40" cy="50" r="4" fill="#333"/>
              <circle cx="60" cy="50" r="4" fill="#333"/>
            </>
          )}
          
          {/* Nose */}
          <ellipse cx="50" cy="60" rx="3" ry="2" fill="#E6C99A"/>
          
          {/* Mouth */}
          {expression === 'happy' ? (
            <path d="M 40 70 Q 50 78, 60 70" stroke="#333" strokeWidth="2" fill="none" strokeLinecap="round"/>
          ) : expression === 'excited' ? (
            <ellipse cx="50" cy="72" rx="8" ry="6" fill="#333"/>
          ) : (
            <path d="M 40 70 Q 50 72, 60 70" stroke="#333" strokeWidth="2" fill="none" strokeLinecap="round"/>
          )}
        </svg>
      );
    }
    
    // Neutral/Default character
    return (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        {/* Face */}
        <circle cx="50" cy="50" r="45" fill="#FFD89B" stroke="#E6C089" strokeWidth="2"/>
        
        {/* Simple hair */}
        <ellipse cx="50" cy="30" rx="35" ry="20" fill="#6B4423"/>
        
        {/* Eyes */}
        {expression === 'happy' ? (
          <>
            <path d="M 35 45 Q 40 50, 35 55" stroke="#333" strokeWidth="2" fill="none" strokeLinecap="round"/>
            <path d="M 65 45 Q 70 50, 65 55" stroke="#333" strokeWidth="2" fill="none" strokeLinecap="round"/>
          </>
        ) : (
          <>
            <circle cx="40" cy="50" r="4" fill="#333"/>
            <circle cx="60" cy="50" r="4" fill="#333"/>
          </>
        )}
        
        {/* Nose */}
        <ellipse cx="50" cy="60" rx="3" ry="2" fill="#E6C089"/>
        
        {/* Mouth */}
        {expression === 'happy' ? (
          <path d="M 40 70 Q 50 78, 60 70" stroke="#333" strokeWidth="2" fill="none" strokeLinecap="round"/>
        ) : (
          <path d="M 40 70 Q 50 72, 60 70" stroke="#333" strokeWidth="2" fill="none" strokeLinecap="round"/>
        )}
      </svg>
    );
  };

  const containerClass = `${sizeClasses[size]} rounded-full overflow-hidden bg-gradient-to-br from-aurora-purple/20 to-aurora-blue/20 border-2 border-aurora-purple/30`;

  if (animated) {
    return (
      <motion.div
        className={containerClass}
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 15 }}
        whileHover={{ scale: 1.1 }}
      >
        {getCharacterSVG()}
      </motion.div>
    );
  }

  return (
    <div className={containerClass}>
      {getCharacterSVG()}
    </div>
  );
};

export default PlaygroundCharacter;
